// DPoP (RFC 9449) key pair generation and proof creation using the Web Crypto API.
// The private key is generated as non-extractable — it cannot be serialized, exported,
// or moved out of the browser's secure key storage. This is intentional: the key
// lives only in Redux memory and is lost on page refresh; whether that requires
// re-authentication depends on backend enforcement (soft vs. enforced DPoP mode).

// Generates a P-256 ECDSA key pair for DPoP signing.
export const generateDpopKeyPair = () =>
  window.crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false, // non-extractable: private key cannot be exported or persisted
    ['sign', 'verify'],
  )

// Exports only the public key portion as a JWK for embedding in DPoP proof headers.
export const exportPublicKeyAsJwk = publicKey => window.crypto.subtle.exportKey('jwk', publicKey)

// Converts an ArrayBuffer to a base64url string without padding (RFC 4648 §5).
// Uses Array.from().reduce() instead of spread (...array) to avoid "Maximum call
// stack size exceeded" on large buffers (spread has a per-call argument limit).
const toBase64Url = buffer => {
  const str = Array.from(new Uint8Array(buffer)).reduce((acc, byte) => acc + String.fromCharCode(byte), '')
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

// Encodes an object to a base64url JSON string for use as a JWT header/payload segment.
const encodeSegment = obj => {
  const bytes = new TextEncoder().encode(JSON.stringify(obj))
  return toBase64Url(bytes)
}

// Creates a signed DPoP proof JWT (RFC 9449 §4.2) for a single HTTP request.
// keyPair     — CryptoKeyPair from generateDpopKeyPair()
// method      — HTTP method in uppercase ('POST', 'GET', etc.)
// url         — full request URL without query string (must match what the backend receives)
// accessToken — when provided, adds the ath claim: base64url(SHA-256(token)).
//               Required for post-login authenticated requests (RFC 9449 §4.2).
//               Omit for login proofs (no bearer token exists yet).
export const createDpopProof = async (keyPair, method, url, accessToken = null) => {
  const jwk = await exportPublicKeyAsJwk(keyPair.publicKey)

  // RFC 9449 §4.2: only kty/crv/x/y members are included in the proof header JWK.
  const header = {
    alg: 'ES256',
    typ: 'dpop+jwt',
    jwk: { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y },
  }

  // jti: cryptographically random 128-bit value for replay protection.
  const jtiBytes = new Uint8Array(16)
  window.crypto.getRandomValues(jtiBytes)

  const payload = {
    jti: toBase64Url(jtiBytes),
    htm: method.toUpperCase(), // HTTP Method claim
    htu: url, // HTTP URI claim — no query string or fragment
    iat: Math.floor(Date.now() / 1000), // issued-at in Unix seconds
  }

  // ath: binds the proof to the specific access token being presented (RFC 9449 §4.2).
  // Prevents replay of a valid proof alongside a different token bound to the same key.
  if (accessToken) {
    const tokenBytes = new TextEncoder().encode(accessToken)
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', tokenBytes)
    payload.ath = toBase64Url(hashBuffer)
  }

  const signingInput = `${encodeSegment(header)}.${encodeSegment(payload)}`

  // SubtleCrypto ECDSA returns IEEE P1363 format (raw r||s, 64 bytes for P-256),
  // which is directly base64url-encodable per RFC 7518 §3.4. No DER conversion needed.
  const signatureBuffer = await window.crypto.subtle.sign(
    { name: 'ECDSA', hash: { name: 'SHA-256' } },
    keyPair.privateKey,
    new TextEncoder().encode(signingInput),
  )

  return `${signingInput}.${toBase64Url(signatureBuffer)}`
}
